import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from './axiosInstance';
import './styles/DuplicateMacRemediation.css';

const DuplicateMacRemediation = ({ globalCompany, globalStores, globalSelectedStore }) => {
    const [mode, setMode] = useState('lookup'); // 'lookup' or 'csv'

    // Lookup mode — company/store come from the GAC top-bar global selectors via props
    const [days, setDays] = useState(28);

    // CSV mode
    const [delimiter, setDelimiter] = useState(',');
    const [csvRows, setCsvRows] = useState([]); // [{companyCode, storeCode, labelCode}]
    const [csvFileName, setCsvFileName] = useState('');
    const [csvError, setCsvError] = useState(null);
    const csvInputRef = useRef(null);

    // SaaS network filter — 'offline' (fast, default), 'online', or 'all'
    const [networkFilter, setNetworkFilter] = useState('offline');

    // Results / progress
    const [comparing, setComparing] = useState(false);
    const [progress, setProgress] = useState(null); // { completed, total, completedGroups, totalGroups, ... }
    const [rows, setRows] = useState(null);
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState(null);
    const pollTimerRef = useRef(null);

    // Filtering / selection
    const [offlineOnly, setOfflineOnly] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [selected, setSelected] = useState(new Set()); // keys "company:store:label"
    const [sort, setSort] = useState({ column: 'scannedAt', direction: 'desc' });

    // Delete state
    const [deleting, setDeleting] = useState(false);
    const [deleteResult, setDeleteResult] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const resolveDelimiter = (d) => (d === '\\t' ? '\t' : d);

    const onCsvSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCsvFileName(file.name);
        setCsvError(null);

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const text = String(ev.target.result || '');
                const d = resolveDelimiter(delimiter);
                const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
                if (lines.length === 0) {
                    setCsvError('CSV is empty');
                    setCsvRows([]);
                    return;
                }

                // Detect header
                const firstFields = lines[0].split(d).map(s => s.trim().toLowerCase());
                const hasHeader = firstFields.some(f => /company|store|label/.test(f));
                const dataLines = hasHeader ? lines.slice(1) : lines;

                const parsed = [];
                for (let i = 0; i < dataLines.length; i++) {
                    const cols = dataLines[i].split(d).map(s => s.trim());
                    if (cols.length < 3) continue;
                    const [companyCode, storeCode, labelCode] = cols;
                    if (!companyCode || !storeCode || !labelCode) continue;
                    parsed.push({ companyCode, storeCode, labelCode });
                }

                if (parsed.length === 0) {
                    setCsvError('No valid rows found. Expected 3 columns: companyCode, storeCode, labelCode');
                }
                setCsvRows(parsed);
            } catch (err) {
                setCsvError(err.message || 'Failed to parse CSV');
                setCsvRows([]);
            }
        };
        reader.readAsText(file);
    };

    const downloadSampleCsv = () => {
        const d = resolveDelimiter(delimiter);
        const sample = [
            ['companyCode', 'storeCode', 'labelCode'].join(d),
            ['E89', 'EO89', '0017340012AB'].join(d),
            ['E89', 'EO89', '0017340012CD'].join(d),
            ['WFC', 'WFC123', '0023A4B5C6D7'].join(d)
        ].join('\n');
        const blob = new Blob([sample], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'duplicate_mac_sample.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const stopPolling = () => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    };

    useEffect(() => () => stopPolling(), []);

    const pollJob = async (jobId) => {
        try {
            const { data } = await axios.get(`global-action/actions/duplicate-mac/compare/status/${jobId}`);
            setProgress(data.summary);

            if (data.status === 'complete') {
                stopPolling();
                setRows(data.rows || []);
                setSummary(data.summary || null);
                setComparing(false);
            } else if (data.status === 'failed') {
                stopPolling();
                setError(data.error || 'Compare failed');
                setComparing(false);
            } else {
                pollTimerRef.current = setTimeout(() => pollJob(jobId), 1500);
            }
        } catch (err) {
            stopPolling();
            setError(err.response?.data?.message || err.message || 'Polling failed');
            setComparing(false);
        }
    };

    const runCompare = async () => {
        stopPolling();
        setError(null);
        setRows(null);
        setSummary(null);
        setProgress(null);
        setSelected(new Set());
        setDeleteResult(null);
        setConfirmDelete(false);
        setComparing(true);

        try {
            const body = mode === 'csv'
                ? { csvRows, networkFilter }
                : {
                    companyCode: globalCompany?.companyCode || globalCompany?.code,
                    storeCode: globalSelectedStore?.store || undefined,
                    days,
                    networkFilter
                };

            if (mode === 'csv' && csvRows.length === 0) {
                setError('Upload a CSV with at least one row first.');
                setComparing(false);
                return;
            }
            if (mode === 'lookup' && !body.companyCode) {
                setError('Select a company in the top bar first.');
                setComparing(false);
                return;
            }

            const { data } = await axios.post('global-action/actions/duplicate-mac/compare/start', body);
            if (!data.jobId) {
                setError(data.message || 'Failed to start compare');
                setComparing(false);
                return;
            }
            setProgress({ total: data.total, totalGroups: data.totalGroups, completed: 0, completedGroups: 0, online: 0, offline: 0, notFound: 0, errors: 0, progress: 0 });
            pollJob(data.jobId);
        } catch (err) {
            console.error('[Duplicate MAC] compare start failed:', err);
            setError(err.response?.data?.message || err.message || 'Compare failed');
            setComparing(false);
        }
    };

    // When SaaS query mode was 'offline', the table only ever needs to show offline rows
    // (the rest are inferred-online and not actionable). The toolbar checkbox is hidden in
    // that mode. When mode is 'all', the user can toggle the checkbox manually.
    const effectiveOfflineOnly = (summary?.networkFilter === 'offline') ? true : offlineOnly;

    const sortValue = (r, col) => {
        switch (col) {
            case 'status':           return r.status || '';
            case 'scannedAt':        return r.scannedAt ? new Date(r.scannedAt).getTime() : 0;
            case 'companyCode':      return r.companyCode || '';
            case 'storeCode':        return r.storeCode || '';
            case 'labelCode':        return r.labelCode || '';
            case 'saasArticleId':    return r.saasArticleId || '';
            case 'historyArticleId': return r.historyArticleId || '';
            case 'user':             return (r.userName || r.userEmail || '').toLowerCase();
            case 'networkStatus':    return r.networkStatus === true ? 1 : r.networkStatus === false ? 0 : -1;
            case 'gateway':          return r.gateway || '';
            default:                 return '';
        }
    };

    const filteredRows = useMemo(() => {
        if (!rows) return [];
        const term = searchFilter.trim().toLowerCase();
        const out = rows.filter(r => {
            if (effectiveOfflineOnly && r.status !== 'offline') return false;
            if (term) {
                const hay = [
                    r.companyCode, r.storeCode, r.labelCode,
                    r.userName, r.userEmail, r.saasArticleId,
                    r.historyArticleId, r.gateway
                ].filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(term)) return false;
            }
            return true;
        });

        const dir = sort.direction === 'asc' ? 1 : -1;
        out.sort((a, b) => {
            const av = sortValue(a, sort.column);
            const bv = sortValue(b, sort.column);
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
        return out;
    }, [rows, effectiveOfflineOnly, searchFilter, sort]);

    const toggleSort = (col) => {
        setSort(prev => prev.column === col
            ? { column: col, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
            : { column: col, direction: 'asc' }
        );
    };

    const sortIndicator = (col) => sort.column === col
        ? (sort.direction === 'asc' ? ' ▲' : ' ▼')
        : '';

    const visibleOfflineRows = useMemo(
        () => filteredRows.filter(r => r.status === 'offline'),
        [filteredRows]
    );

    const rowKey = (r) => `${r.companyCode}:${r.storeCode}:${r.labelCode}`;

    const toggleRow = (r) => {
        if (r.status !== 'offline') return; // only offline are selectable
        const k = rowKey(r);
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    };

    const allVisibleOfflineSelected = visibleOfflineRows.length > 0
        && visibleOfflineRows.every(r => selected.has(rowKey(r)));

    const toggleSelectAllVisibleOffline = () => {
        setSelected(prev => {
            const next = new Set(prev);
            if (allVisibleOfflineSelected) {
                visibleOfflineRows.forEach(r => next.delete(rowKey(r)));
            } else {
                visibleOfflineRows.forEach(r => next.add(rowKey(r)));
            }
            return next;
        });
    };

    const exportEnrichedCsv = () => {
        if (!rows) return;
        const header = [
            'Date (UTC)', 'Company', 'Store', 'Label Code', 'SaaS Status',
            'Network Status', 'SaaS Article ID', 'History Article ID',
            'User Name', 'User Email', 'Assign Type', 'Assign Success', 'Assign Error',
            'Gateway', 'Battery', 'Signal', 'Last Response Time', 'Label Model', 'Label Type'
        ];
        const escape = (v) => {
            if (v === null || v === undefined) return '';
            const s = String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const lines = [header.map(escape).join(',')];
        for (const r of filteredRows) {
            lines.push([
                r.scannedAt ? new Date(r.scannedAt).toISOString() : '',
                r.companyCode, r.storeCode, r.labelCode, r.status,
                r.networkStatus || '',
                r.saasArticleId || '', r.historyArticleId || '',
                r.userName || '', r.userEmail || '', r.assignType || '',
                r.assignSuccess === false ? 'false' : (r.assignSuccess ? 'true' : ''),
                r.assignError || '',
                r.gateway || '', r.battery || '', r.signal || '',
                r.lastResponseTime ? new Date(r.lastResponseTime).toISOString() : '',
                r.labelModel || '', r.labelType || ''
            ].map(escape).join(','));
        }
        const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        a.download = `duplicate_mac_remediation_${ts}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const runDelete = async () => {
        const items = Array.from(selected).map(k => {
            const [companyCode, storeCode, labelCode] = k.split(':');
            return { companyCode, storeCode, labelCode };
        });
        if (items.length === 0) return;

        setDeleting(true);
        setDeleteResult(null);
        try {
            const { data } = await axios.post('global-action/actions/duplicate-mac/delete-offline', { items }, { timeout: 600000 });
            setDeleteResult(data);
            // Update rows to reflect deletes (mark as deleted)
            const deletedKeys = new Set(items.map(i => `${i.companyCode}:${i.storeCode}:${i.labelCode}`));
            setRows(prev => prev?.map(r => deletedKeys.has(rowKey(r))
                ? { ...r, status: 'deleted' }
                : r
            ));
            setSelected(new Set());
            setConfirmDelete(false);
        } catch (err) {
            setDeleteResult({
                success: false,
                message: err.response?.data?.message || err.message || 'Delete failed'
            });
        } finally {
            setDeleting(false);
        }
    };

    const statusBadge = (status) => {
        const cls = `dmr-status dmr-status-${status}`;
        const map = {
            online: 'Online',
            offline: 'Offline',
            'not-found': 'Not Found',
            online_or_missing: 'Online',
            offline_or_missing: 'Offline',
            error: 'Error',
            deleted: 'Deleted'
        };
        return <span className={cls}>{map[status] || status}</span>;
    };

    return (
        <div className="dmr-container">
            <div className="dmr-config">
                <div className="dmr-mode-toggle">
                    <button
                        className={`dmr-mode-btn ${mode === 'lookup' ? 'active' : ''}`}
                        onClick={() => setMode('lookup')}
                    >
                        By Company / Store + Date Range
                    </button>
                    <button
                        className={`dmr-mode-btn ${mode === 'csv' ? 'active' : ''}`}
                        onClick={() => setMode('csv')}
                    >
                        Upload CSV
                    </button>
                </div>

                {mode === 'lookup' && (
                    <div className="dmr-lookup-row">
                        <div className="dmr-field">
                            <label>Lookback (days):</label>
                            <input
                                type="number"
                                min="1"
                                max="365"
                                value={days}
                                onChange={(e) => setDays(parseInt(e.target.value, 10) || 28)}
                            />
                        </div>

                        <div className="dmr-field dmr-field-filter">
                            <label>SaaS query:</label>
                            <div className="dmr-radio-group">
                                <label className="dmr-radio">
                                    <input
                                        type="radio"
                                        name="dmr-net"
                                        value="offline"
                                        checked={networkFilter === 'offline'}
                                        onChange={() => setNetworkFilter('offline')}
                                    />
                                    Offline only
                                </label>
                                <label className="dmr-radio">
                                    <input
                                        type="radio"
                                        name="dmr-net"
                                        value="all"
                                        checked={networkFilter === 'all'}
                                        onChange={() => setNetworkFilter('all')}
                                    />
                                    All labels
                                </label>
                            </div>
                        </div>

                        <div className="dmr-field dmr-field-action">
                            <button
                                className="dmr-btn dmr-btn-primary"
                                onClick={runCompare}
                                disabled={comparing || !globalCompany}
                            >
                                {comparing ? 'Comparing...' : 'Run Compare'}
                            </button>
                        </div>
                    </div>
                )}

                {mode === 'csv' && (
                    <div className="dmr-lookup-row">
                        <div className="dmr-field">
                            <label>Delimiter:</label>
                            <select value={delimiter} onChange={(e) => setDelimiter(e.target.value)}>
                                <option value=",">Comma  ( , )</option>
                                <option value=";">Semicolon  ( ; )</option>
                                <option value="\t">Tab</option>
                                <option value="|">Pipe  ( | )</option>
                            </select>
                        </div>

                        <div className="dmr-field dmr-field-csv">
                            <label>CSV file (companyCode, storeCode, labelCode):</label>
                            <div className="dmr-csv-actions">
                                <input
                                    type="file"
                                    ref={csvInputRef}
                                    accept=".csv,.txt"
                                    onChange={onCsvSelect}
                                    style={{ display: 'none' }}
                                />
                                <button className="dmr-btn" onClick={() => csvInputRef.current?.click()}>
                                    Choose File
                                </button>
                                <button className="dmr-btn dmr-btn-secondary" onClick={downloadSampleCsv}>
                                    📥 Sample
                                </button>
                                {csvFileName && (
                                    <span className="dmr-csv-name">
                                        <strong>{csvFileName}</strong>
                                        {csvRows.length > 0 && <> — {csvRows.length.toLocaleString()} row(s)</>}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="dmr-field dmr-field-filter">
                            <label>SaaS query:</label>
                            <div className="dmr-radio-group">
                                <label className="dmr-radio">
                                    <input
                                        type="radio"
                                        name="dmr-net"
                                        value="offline"
                                        checked={networkFilter === 'offline'}
                                        onChange={() => setNetworkFilter('offline')}
                                    />
                                    Offline only
                                </label>
                                <label className="dmr-radio">
                                    <input
                                        type="radio"
                                        name="dmr-net"
                                        value="all"
                                        checked={networkFilter === 'all'}
                                        onChange={() => setNetworkFilter('all')}
                                    />
                                    All labels
                                </label>
                            </div>
                        </div>

                        <div className="dmr-field dmr-field-action">
                            <button
                                className="dmr-btn dmr-btn-primary"
                                onClick={runCompare}
                                disabled={comparing || csvRows.length === 0}
                            >
                                {comparing ? 'Comparing...' : 'Run Compare'}
                            </button>
                        </div>
                    </div>
                )}

                {csvError && <div className="dmr-error">{csvError}</div>}

                {comparing && progress && (
                    <div className="dmr-progress">
                        <div className="dmr-progress-bar">
                            <div
                                className="dmr-progress-fill"
                                style={{ width: `${progress.progress || 0}%` }}
                            />
                        </div>
                        <div className="dmr-progress-text">
                            {progress.completedGroups || 0} / {progress.totalGroups || 0} stores —
                            {' '}{progress.completed || 0} / {progress.total || 0} labels processed
                            {' '}({progress.progress || 0}%)
                        </div>
                    </div>
                )}

                {error && <div className="dmr-error">{error}</div>}
            </div>

            {summary && (
                <div className="dmr-summary">
                    <div className="dmr-summary-card info">
                        <div className="label">Total</div>
                        <div className="value">{summary.total.toLocaleString()}</div>
                    </div>
                    <div className="dmr-summary-card success">
                        <div className="label">Online</div>
                        <div className="value">{summary.online.toLocaleString()}</div>
                    </div>
                    <div className="dmr-summary-card warn">
                        <div className="label">Offline</div>
                        <div className="value">{summary.offline.toLocaleString()}</div>
                    </div>
                    <div className="dmr-summary-card neutral">
                        <div className="label">Not Found</div>
                        <div className="value">{summary.notFound.toLocaleString()}</div>
                    </div>
                </div>
            )}

            {rows && rows.length > 0 && (
                <div className="dmr-results">
                    <div className="dmr-toolbar">
                        {summary?.networkFilter === 'all' && (
                            <label className="dmr-checkbox">
                                <input
                                    type="checkbox"
                                    checked={offlineOnly}
                                    onChange={(e) => setOfflineOnly(e.target.checked)}
                                />
                                Show offline only
                            </label>
                        )}

                        <input
                            type="text"
                            className="dmr-search"
                            placeholder="Filter by label, store, user, gateway..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                        />

                        <div className="dmr-toolbar-spacer" />

                        <button className="dmr-btn dmr-btn-secondary" onClick={exportEnrichedCsv}>
                            ⬇ Export CSV
                        </button>

                        <button
                            className="dmr-btn dmr-btn-danger"
                            disabled={selected.size === 0 || deleting}
                            onClick={() => setConfirmDelete(true)}
                        >
                            Delete Selected ({selected.size})
                        </button>
                    </div>

                    <div className="dmr-table-wrap">
                        <table className="dmr-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 32 }}>
                                        <input
                                            type="checkbox"
                                            checked={allVisibleOfflineSelected}
                                            onChange={toggleSelectAllVisibleOffline}
                                            disabled={visibleOfflineRows.length === 0}
                                            title="Select all visible offline labels"
                                        />
                                    </th>
                                    <th className="dmr-sortable" onClick={() => toggleSort('status')}>Status{sortIndicator('status')}</th>
                                    <th className="dmr-sortable" onClick={() => toggleSort('scannedAt')}>Date (UTC){sortIndicator('scannedAt')}</th>
                                    <th className="dmr-sortable" onClick={() => toggleSort('companyCode')}>Company{sortIndicator('companyCode')}</th>
                                    <th className="dmr-sortable" onClick={() => toggleSort('storeCode')}>Store{sortIndicator('storeCode')}</th>
                                    <th className="dmr-sortable" onClick={() => toggleSort('labelCode')}>Label Code{sortIndicator('labelCode')}</th>
                                    <th className="dmr-sortable" onClick={() => toggleSort('saasArticleId')}>SaaS Article{sortIndicator('saasArticleId')}</th>
                                    <th className="dmr-sortable" onClick={() => toggleSort('historyArticleId')}>History Article{sortIndicator('historyArticleId')}</th>
                                    <th className="dmr-sortable" onClick={() => toggleSort('user')}>User{sortIndicator('user')}</th>
                                    <th className="dmr-sortable" onClick={() => toggleSort('networkStatus')}>Network{sortIndicator('networkStatus')}</th>
                                    <th className="dmr-sortable" onClick={() => toggleSort('gateway')}>Gateway{sortIndicator('gateway')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.slice(0, 500).map((r, idx) => (
                                    <tr key={`${rowKey(r)}-${idx}`} className={`dmr-row dmr-row-${r.status}`}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selected.has(rowKey(r))}
                                                onChange={() => toggleRow(r)}
                                                disabled={r.status !== 'offline'}
                                            />
                                        </td>
                                        <td>{statusBadge(r.status)}</td>
                                        <td>{r.scannedAt ? new Date(r.scannedAt).toLocaleString() : ''}</td>
                                        <td>{r.companyCode}</td>
                                        <td>{r.storeCode}</td>
                                        <td className="dmr-mono">{r.labelCode}</td>
                                        <td className="dmr-mono">{r.saasArticleId || ''}</td>
                                        <td className="dmr-mono">{r.historyArticleId || ''}</td>
                                        <td>
                                            {r.userName || ''}
                                            {r.userEmail && <div className="dmr-muted">{r.userEmail}</div>}
                                        </td>
                                        <td>{r.networkStatus || ''}</td>
                                        <td>{r.gateway || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredRows.length > 500 && (
                            <div className="dmr-truncated">
                                Showing first 500 of {filteredRows.length.toLocaleString()} matching rows. Use the search filter or export the full CSV.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {rows && rows.length === 0 && (
                <div className="dmr-empty">No rows returned for this query.</div>
            )}

            {confirmDelete && (
                <div className="dmr-modal-backdrop">
                    <div className="dmr-modal">
                        <h4>Confirm SaaS Deletion</h4>
                        <p>
                            You're about to delete <strong>{selected.size}</strong> offline label(s) from SaaS.
                            This is permanent and will be logged in AuditLog.
                        </p>
                        <div className="dmr-modal-actions">
                            <button className="dmr-btn" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                                Cancel
                            </button>
                            <button className="dmr-btn dmr-btn-danger" onClick={runDelete} disabled={deleting}>
                                {deleting ? 'Deleting...' : `Delete ${selected.size} Label(s)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteResult && (
                <div className={`dmr-delete-result ${deleteResult.success ? 'success' : 'error'}`}>
                    <strong>{deleteResult.success ? '✓ Delete complete' : '✗ Delete had failures'}</strong>
                    {deleteResult.summary && (
                        <span>
                            {' '} — Deleted: {deleteResult.summary.totalDeleted}, Failed: {deleteResult.summary.totalFailed}, Groups: {deleteResult.summary.totalGroups}
                        </span>
                    )}
                    {deleteResult.message && <div>{deleteResult.message}</div>}
                </div>
            )}
        </div>
    );
};

export default DuplicateMacRemediation;
